import { useState, useEffect } from 'react';
import { Loader2, FileText, Download, AlertCircle, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../foundation/altzero_base/base/components/ui/dialog';
import { Button } from '../../../../foundation/altzero_base/base/components/ui/button';
import { billEmailService } from '../../services/billEmailService';
import type { EmailSuggestion, GmailPreview, GmailPreviewAttachment } from '../../services/billEmailService';
import { isBrowserRenderable, describeAttachmentType } from '../../utils/attachmentPreview';

type Props = {
  open: boolean;
  onClose: () => void;
  suggestion: EmailSuggestion | null;
  onAdded: () => void;
};

function fmtSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Full-size, read-only preview of a live Gmail search suggestion — subject,
 * body, attachments, all fetched straight from Gmail. Nothing is written to
 * our DB until "Add to Inbox" is clicked. This is the "make sure it's the
 * right one before you commit to it" step the plain dropdown row can't give.
 */
export function SuggestionPreviewDialog({ open, onClose, suggestion, onAdded }: Props) {
  const [preview, setPreview] = useState<GmailPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeAttachment, setActiveAttachment] = useState<GmailPreviewAttachment | null>(null);
  const [attachmentUrl, setAttachmentUrl] = useState<string | null>(null);
  const [attachmentMime, setAttachmentMime] = useState<string | null>(null);
  const [attachmentLoading, setAttachmentLoading] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  // Kept apart from `error`, which means "the preview itself would not load".
  // A failed add must leave the email on screen and the button clickable —
  // replacing the whole dialog with one red line gives no way to retry.
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !suggestion) return;
    setLoading(true);
    setPreview(null);
    setActiveAttachment(null);
    setAttachmentUrl(null);
    setAttachmentMime(null);
    setError(null);
    setAddError(null);
    billEmailService.previewGmail(suggestion.account_id, suggestion.gmail_message_id).then(({ success, data, error: err }) => {
      if (success && data) {
        setPreview(data);
        if (data.attachments[0]) setActiveAttachment(data.attachments[0]);
      } else {
        setError(err ?? 'Failed to load email');
      }
      setLoading(false);
    });
  }, [open, suggestion]);

  useEffect(() => {
    if (!open || !suggestion || !activeAttachment) return;
    let revoke: string | null = null;
    setAttachmentLoading(true);
    setAttachmentError(null);
    setAttachmentUrl(null);
    setAttachmentMime(null);
    billEmailService.previewGmailAttachmentBlob(suggestion.account_id, suggestion.gmail_message_id, activeAttachment)
      .then(({ success, data, error: err }) => {
        setAttachmentLoading(false);
        if (success && data) {
          const url = URL.createObjectURL(data);
          revoke = url;
          setAttachmentUrl(url);
          setAttachmentMime(data.type || activeAttachment.mime_type);
        } else {
          setAttachmentError(err ?? 'Failed to load attachment');
        }
      });
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [open, suggestion, activeAttachment]);

  const handleAdd = async () => {
    if (!suggestion) return;
    setAdding(true);
    setAddError(null);
    const { success, data, error: err } = await billEmailService.ingestSelected(
      suggestion.account_id, suggestion.gmail_message_id,
    );
    setAdding(false);
    // Job 65: the server now answers `success: false` when nothing was stored —
    // a filtered message, or an insert the database rejected. Previously every
    // one of those came back as a success and this closed on an empty add.
    if (!success) {
      setAddError(err ?? 'Failed to add to inbox');
      return;
    }

    // Job 62: the message was already stored, so this rebuilt its search index
    // rather than adding a row. Say so, and stay open when there was nothing to
    // recover — closing the dialog on a no-op is what made this look like it
    // had worked when it had not.
    if (data?.status === 'reindexed' && data.pdfTextEmpty) {
      setAddError(
        'This email is already in the inbox, and its PDF has no text layer — most '
        + 'likely a scanned invoice. Its NMI cannot be made searchable this way; '
        + 'find the email by sender or date and assign it by hand.',
      );
      return;
    }

    onAdded();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="suggestion-preview" className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Preview from Gmail</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div data-testid="suggestion-preview-loading" className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : error ? (
          <div data-testid="suggestion-preview-error" className="mt-4 flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4" /> {error}
          </div>
        ) : preview && (
          <div className="mt-4 space-y-4">
            <div className="border-b border-gray-100 pb-3 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-base font-semibold text-gray-900">{preview.subject || '(no subject)'}</p>
                <p className="text-sm text-gray-500 mt-0.5">
                  {preview.sender_name ? `${preview.sender_name} · ` : ''}{preview.sender_email} · {new Date(preview.received_at).toLocaleString('en-AU')}
                </p>
              </div>
              <Button data-testid="suggestion-add-top" size="sm" onClick={handleAdd} disabled={adding} className="gap-1.5 shrink-0">
                {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                Add to Inbox
              </Button>
            </div>

            <div className="rounded-lg border border-gray-200 overflow-hidden">
              {preview.bodyHtml ? (
                <iframe
                  srcDoc={preview.bodyHtml}
                  sandbox=""
                  title="Email body"
                  className="w-full bg-white"
                  style={{ height: 340 }}
                />
              ) : (
                <pre className="text-sm text-gray-700 whitespace-pre-wrap p-4 max-h-80 overflow-y-auto bg-gray-50">
                  {preview.bodyText || '(no message content)'}
                </pre>
              )}
            </div>

            {preview.attachments.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Attachments ({preview.attachments.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {preview.attachments.map((a) => (
                    <button
                      key={a.gmail_attachment_id}
                      onClick={() => setActiveAttachment(a)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                        activeAttachment?.gmail_attachment_id === a.gmail_attachment_id
                          ? 'border-blue-300 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <FileText className="h-3.5 w-3.5" />
                      {a.filename}
                      {a.size_bytes != null && <span className="text-gray-400">({fmtSize(a.size_bytes)})</span>}
                    </button>
                  ))}
                </div>

                <div className="rounded-lg border border-gray-200 bg-gray-50 min-h-[420px] flex items-center justify-center overflow-hidden">
                  {attachmentLoading ? (
                    <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
                  ) : attachmentError ? (
                    <p className="text-sm text-red-600 px-4 text-center">{attachmentError}</p>
                  ) : attachmentUrl && isBrowserRenderable(attachmentMime) ? (
                    <iframe src={attachmentUrl} title="Attachment preview" className="w-full" style={{ height: 420 }} />
                  ) : attachmentUrl ? (
                    // Framing a type the browser can't render downloads it instead.
                    <div className="flex flex-col items-center gap-2 text-center px-6">
                      <FileText className="h-9 w-9 text-gray-300" />
                      <p className="text-sm font-medium text-gray-700">
                        {describeAttachmentType(attachmentMime)} can't be previewed in the browser
                      </p>
                      <p className="text-xs text-gray-500">Download it below to check the contents.</p>
                    </div>
                  ) : null}
                </div>

                {attachmentUrl && (
                  <a
                    href={attachmentUrl}
                    download={activeAttachment?.filename ?? 'attachment'}
                    className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline"
                  >
                    <Download className="h-3.5 w-3.5" /> Download
                  </a>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-6 border border-dashed border-gray-200 rounded-lg">
                No attachments on this email.
              </p>
            )}

            {addError && (
              <div data-testid="suggestion-add-error" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{addError}</span>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button data-testid="suggestion-add" onClick={handleAdd} disabled={adding} className="gap-1.5">
                {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Add to Inbox
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
